/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useEmailTemplateSubmit.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the email-templates module.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import {
    EmailTemplate,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate
} from '../api/emailTemplatesApi';

export const useEmailTemplateSubmit = (
    formData: Partial<EmailTemplate>,
    baseline: Partial<EmailTemplate>,
    hasChanges: boolean,
    validateForm: () => boolean,
    mode: 'create' | 'edit',
    id?: string,
    setFormData?: React.Dispatch<React.SetStateAction<Partial<EmailTemplate>>>,
    setBaseline?: React.Dispatch<React.SetStateAction<Partial<EmailTemplate>>>
) => {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const submitTemplate = useCallback(async (): Promise<EmailTemplate | undefined> => {
        if (!validateForm()) return;
      // hasChanges validation removed

        setIsSaving(true);
        try {
            let result: EmailTemplate;
            if (mode === 'create') {
                result = await createEmailTemplate(formData);
                showSuccess(t('emailTemplates.messages.createSuccess'));
            } else {
                result = await updateEmailTemplate(id!, formData);
                showSuccess(t('emailTemplates.messages.updateSuccess'));
            }

            if (setBaseline) setBaseline(result);
            return result;
        } catch (error) {
            console.error('Error saving email template:', error);
            showError(t('emailTemplates.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [formData, hasChanges, validateForm, mode, id, showSuccess, showError, t, setBaseline]);

    const handleCancel = useCallback(() => {
        // Reset form to baseline instead of navigating away
        // Email templates can only be edited, not created, so Cancel means "discard changes"
        if (setFormData && baseline) {
            setFormData({ ...baseline });
        }
    }, [setFormData, baseline]);

    const handleDelete = useCallback(async () => {
        if (!id) return;
        setIsDeleting(true);
        try {
            await deleteEmailTemplate(id);
            showSuccess(t('emailTemplates.messages.deleteSuccess'));
            navigate('/admin/system-settings/email-template');
        } catch (error) {
            console.error('Error deleting email template:', error);
            showError(t('emailTemplates.messages.deleteError'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [id, navigate, showSuccess, showError, t]);

    return {
        isSaving,
        isDeleting,
        showDeleteModal,
        setShowDeleteModal,
        submitTemplate,
        handleCancel,
        handleDelete
    };
};
